import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppPageShell from "../components/AppPageShell";
import AppSectionCard from "../components/AppSectionCard";
import { pageStyles } from "../styles/appTheme";
import LocalLibrarianPanel from "../components/LocalLibrarianPanel";
function normalizeStatusData(data) {
    if (!data)
        return data;
    // If coming from legacy admin_status route
    if (data.brain && data.brain.active_provider !== undefined) {
        return data;
    }
    // If coming from different structure, normalize
    return {
        ...data,
        brain: {
            active_provider: data?.brain?.active_provider || "none",
            fallback_provider: data?.brain?.fallback_provider || "local",
            preferred_provider: data?.brain?.preferred_provider || "avabrain",
            brain_api_online: !!data?.brain?.brain_api_online,
            openai_configured: !!data?.brain?.openai_configured,
            local_brain_enabled: !!data?.brain?.local_brain_enabled,
            brain_base_url: data?.brain?.brain_base_url || null
        }
    };
}
const API_BASE = import.meta.env.VITE_API_BASE || "";
function getCookie(name) {
    if (typeof document === "undefined")
        return null;
    const safe = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const match = document.cookie.match(new RegExp(`(?:^|; )${safe}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}
function getStoredToken() {
    const keys = ["ava_session_token", "ava_token", "token", "access_token"];
    for (const key of keys) {
        const value = localStorage.getItem(key) ||
            sessionStorage.getItem(key) ||
            getCookie(key);
        if (value)
            return value;
    }
    return "";
}
function getStoredUser() {
    const keys = ["ava_user", "user", "profile", "ava_profile", "user_profile"];
    for (const key of keys) {
        try {
            const raw = localStorage.getItem(key) ||
                sessionStorage.getItem(key) ||
                getCookie(key);
            if (!raw)
                continue;
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === "object")
                return parsed;
        }
        catch {
            continue;
        }
    }
    return null;
}
export default function AdminConsole() {
    const navigate = useNavigate();
    const storedUser = useMemo(() => getStoredUser(), []);
    const token = getStoredToken();
    const headers = token
        ? {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        }
        : {
            "Content-Type": "application/json",
        };
    const [status, setStatus] = useState(null);
    const [users, setUsers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [userActivity, setUserActivity] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [actionMsg, setActionMsg] = useState("");
    const [statusWarning, setStatusWarning] = useState("");
    const [usersWarning, setUsersWarning] = useState("");
    const [invitesWarning, setInvitesWarning] = useState("");
    const [elevation, setElevation] = useState(null);
    const [elevationPin, setElevationPin] = useState("");
    const [elevationModalOpen, setElevationModalOpen] = useState(false);
    const [elevationBusy, setElevationBusy] = useState(false);
    const [elevationMessage, setElevationMessage] = useState("");
    const [nowEpoch, setNowEpoch] = useState(() => Math.floor(Date.now() / 1000));
    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteDays, setInviteDays] = useState("5");
    const [selectedEmail, setSelectedEmail] = useState("");
    const [preferredProvider, setPreferredProvider] = useState("avabrain");
    const [localFallbackEnabled, setLocalFallbackEnabled] = useState(false);
    const [adminSection, setAdminSection] = useState("overview");
    const [approvals, setApprovals] = useState([]);
    const [approvalsLoading, setApprovalsLoading] = useState(false);
    const [approvalBusyId, setApprovalBusyId] = useState("");
    const [approvalMessage, setApprovalMessage] = useState("");
    // AVAAIO_OWNER_ATTENTION_UI_V1
    const [ownerAttention, setOwnerAttention] = useState(null);
    async function safeJson(res) {
        return res.json().catch(() => ({}));
    }
    function responseDetail(data, fallback) {
        if (typeof data?.detail === "string")
            return data.detail;
        if (typeof data?.message === "string")
            return data.message;
        return fallback;
    }
    // AVAAIO_OWNER_ATTENTION_UI_V1
    //
    // Read-only aggregate owner-attention poll.
    // Cannot approve, reject, claim, execute, install,
    // repair, reload, restart, or modify production.
    async function loadOwnerAttention(notifyOwner = true) {
        try {
            const res = await fetch(`${API_BASE}/api/v1/admin/owner-attention`, {
                method: "GET",
                credentials: "include",
                headers,
                cache: "no-store",
            });
            const data = await safeJson(res);
            if (!res.ok) {
                setOwnerAttention(null);
                return;
            }
            if (data?.execution_allowed !== false ||
                data?.executor_called !== false) {
                console.warn("[AVAAIO OWNER ATTENTION] Safety contract mismatch.");
                setOwnerAttention(null);
                return;
            }
            const normalized = {
                ok: data?.ok === true,
                attention_required: data?.attention_required === true,
                pending_approvals: Math.max(0, Number(data?.pending_approvals || 0)),
                health_attention_required: data?.health_attention_required === true,
                health_attention_count: Math.max(0, Number(data?.health_attention_count || 0)),
                pending_approvals_capped: data?.pending_approvals_capped === true,
                execution_allowed: false,
                executor_called: false,
            };
            setOwnerAttention(normalized);
            if (typeof window === "undefined" ||
                typeof sessionStorage === "undefined") {
                return;
            }
            const storageKey = "avaaio_owner_attention_last_signature";
            if (!normalized.attention_required) {
                sessionStorage.removeItem(storageKey);
                return;
            }
            if (!notifyOwner) {
                return;
            }
            const signature = [
                normalized.pending_approvals,
                normalized.health_attention_count,
            ].join(":");
            if (sessionStorage.getItem(storageKey) ===
                signature) {
                return;
            }
            sessionStorage.setItem(storageKey, signature);
            const parts = [];
            if (normalized.pending_approvals > 0) {
                parts.push(`${normalized.pending_approvals} pending approval${normalized.pending_approvals === 1 ? "" : "s"}`);
            }
            if (normalized.health_attention_count > 0) {
                parts.push(`${normalized.health_attention_count} health alert${normalized.health_attention_count === 1 ? "" : "s"}`);
            }
            const detail = parts.length > 0
                ? `Ava needs your attention: ${parts.join(", ")}.`
                : "Ava needs your attention.";
            window.dispatchEvent(new CustomEvent("ava-toast", {
                detail,
            }));
        }
        catch (err) {
            console.warn("[AVAAIO OWNER ATTENTION] Poll failed:", err);
        }
    }
    async function loadElevationStatus() {
        try {
            const res = await fetch(`${API_BASE}/api/v1/chat/admin-elevation/status`, {
                method: "GET",
                credentials: "include",
                headers,
            });
            const data = await safeJson(res);
            if (!res.ok) {
                setElevation({
                    elevated: false,
                    eligible: false,
                    elevated_until: null,
                });
                setElevationMessage(responseDetail(data, "Unable to read administrator security status."));
                return;
            }
            setElevation(data);
            if (data?.elevated) {
                await loadAll();
            }
            else {
                setStatus(null);
                setUsers([]);
                setInvites([]);
                setUserActivity(null);
                setLoading(false);
            }
        }
        catch (err) {
            setElevation({
                elevated: false,
                elevated_until: null,
            });
            setElevationMessage(err?.message ||
                "Administrator security status request failed.");
        }
    }
    async function submitElevationPin() {
        const pin = elevationPin.trim();
        if (!pin) {
            setElevationMessage("Enter the separate Admin PIN.");
            return;
        }
        try {
            setElevationBusy(true);
            setElevationMessage("");
            const res = await fetch(`${API_BASE}/api/v1/chat/verify-admin-pin`, {
                method: "POST",
                credentials: "include",
                headers,
                body: JSON.stringify({
                    pin,
                }),
            });
            const data = await safeJson(res);
            if (!res.ok) {
                const retryAfter = res.headers.get("Retry-After");
                setElevationMessage(retryAfter
                    ? `Too many attempts. Try again in ${retryAfter} seconds.`
                    : responseDetail(data, "Administrator verification failed."));
                setElevationPin("");
                await loadElevationStatus();
                return;
            }
            setElevationPin("");
            setElevationModalOpen(false);
            setElevationMessage("Super Admin controls unlocked for ten minutes.");
            await loadElevationStatus();
        }
        catch (err) {
            setElevationMessage(err?.message ||
                "Administrator verification request failed.");
        }
        finally {
            setElevationBusy(false);
        }
    }
    async function lockElevation() {
        try {
            setElevationBusy(true);
            setElevationMessage("");
            const res = await fetch(`${API_BASE}/api/v1/chat/admin-elevation/logout`, {
                method: "POST",
                credentials: "include",
                headers,
                body: JSON.stringify({}),
            });
            const data = await safeJson(res);
            if (!res.ok) {
                setElevationMessage(responseDetail(data, "Unable to lock administrator controls."));
                return;
            }
            sessionStorage.removeItem("ava_admin_pin_verified");
            setElevationPin("");
            setElevationModalOpen(false);
            setElevationMessage("Administrator controls are locked.");
            await loadElevationStatus();
        }
        catch (err) {
            setElevationMessage(err?.message ||
                "Administrator lock request failed.");
        }
        finally {
            setElevationBusy(false);
        }
    }
    const loadAll = async () => {
        try {
            setLoading(true);
            setError("");
            setActionMsg("");
            setStatusWarning("");
            setUsersWarning("");
            setInvitesWarning("");
            const [statusRes, usersRes, invitesRes, activityRes] = await Promise.allSettled([
                fetch(`${API_BASE}/api/v1/admin/system-status-real`, {
                    method: "GET",
                    credentials: "include",
                    headers,
                }),
                fetch(`${API_BASE}/api/v1/admin/users`, {
                    method: "GET",
                    credentials: "include",
                    headers,
                }),
                fetch(`${API_BASE}/api/v1/admin/invites`, {
                    method: "GET",
                    credentials: "include",
                    headers,
                }),
                fetch(`${API_BASE}/api/v1/admin/user-activity`, {
                    method: "GET",
                    credentials: "include",
                    headers,
                }),
            ]);
            if (statusRes.status === "fulfilled") {
                const statusData = await safeJson(statusRes.value);
                if (statusRes.value.ok) {
                    setStatus(normalizeStatusData(statusData));
                    setPreferredProvider(statusData?.brain?.preferred_provider || "avabrain");
                    setLocalFallbackEnabled(Boolean(statusData?.brain?.local_brain_enabled));
                }
                else {
                    setStatusWarning(statusData?.detail || "System status endpoint returned an error.");
                }
            }
            else {
                setStatusWarning("System status request failed.");
            }
            if (usersRes.status === "fulfilled") {
                const usersData = await safeJson(usersRes.value);
                if (usersRes.value.ok) {
                    setUsers(usersData?.users || []);
                }
                else {
                    setUsersWarning(usersData?.detail || "Users endpoint returned an error.");
                }
            }
            else {
                setUsersWarning("Users request failed.");
            }
            if (invitesRes.status === "fulfilled") {
                const invitesData = await safeJson(invitesRes.value);
                if (invitesRes.value.ok) {
                    setInvites(invitesData?.invites || []);
                }
                else {
                    setInvitesWarning(invitesData?.detail || "Invites endpoint returned an error.");
                }
            }
            else {
                setInvitesWarning("Invites request failed.");
            }
            if (activityRes.status === "fulfilled") {
                const activityData = await safeJson(activityRes.value);
                if (activityRes.value.ok) {
                    setUserActivity(activityData || null);
                }
                else {
                    console.warn("User activity endpoint returned an error:", activityData?.detail || activityData);
                    setUserActivity(null);
                }
            }
            else {
                console.warn("User activity request failed.");
                setUserActivity(null);
            }
        }
        catch (err) {
            setError(err?.message || "Admin console failed to load.");
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void loadElevationStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        const timer = window.setInterval(() => {
            setNowEpoch(Math.floor(Date.now() / 1000));
        }, 1000);
        return () => window.clearInterval(timer);
    }, []);
    // AVAAIO_OWNER_ATTENTION_UI_V1
    useEffect(() => {
        void loadOwnerAttention(true);
        const timer = window.setInterval(() => {
            void loadOwnerAttention(true);
        }, 30000);
        return () => {
            window.clearInterval(timer);
        };
        // Intentional mount-level polling.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    async function postAction(path, body, method = "POST") {
        try {
            setActionMsg("");
            setError("");
            const res = await fetch(`${API_BASE}${path}`, {
                method,
                credentials: "include",
                headers,
                body: JSON.stringify(body),
            });
            const data = await safeJson(res);
            if (!res.ok) {
                const detail = responseDetail(data, "Request failed");
                if (res.status === 403 &&
                    (detail.includes("Administrator verification") ||
                        detail.includes("Elevated Super Admin"))) {
                    setElevationMessage("Unlock Super Admin controls to continue.");
                    setElevationModalOpen(true);
                    setActionMsg("🔐 This operation requires the separate Admin PIN.");
                    await loadElevationStatus();
                    return;
                }
                setActionMsg(`❌ API Error: ${detail}`);
                return;
            }
            setActionMsg(data?.message || "✅ Action saved. Refreshing live data...");
            await loadAll();
        }
        catch (err) {
            setActionMsg(err?.message || "Action failed.");
        }
    }
    async function confirmUserAction(label, path, danger = false, method = "POST") {
        if (!selectedEmail) {
            setActionMsg("Select a user first.");
            return;
        }
        const ok = window.confirm(`${danger ? "⚠️ " : ""}${label}\n\nUser: ${selectedEmail}\n\nContinue?`);
        if (!ok) {
            setActionMsg("Action cancelled.");
            return;
        }
        await postAction(path, { email: selectedEmail }, method);
    }
    async function saveBrainControls() {
        await postAction("/api/v1/admin/brain-control", {
            preferred_provider: preferredProvider,
            local_fallback_enabled: localFallbackEnabled,
        });
    }
    async function confirmSelectedUserLive() {
        if (!selectedEmail) {
            setActionMsg("Select a user first.");
            return;
        }
        try {
            const res = await fetch(`${API_BASE}/api/v1/admin/users`, {
                method: "GET",
                credentials: "include",
                headers: {
                    "Content-Type": "application/json",
                    ...(headers || {})
                }
            });
            const data = await safeJson(res);
            if (!res.ok) {
                setActionMsg(`❌ API Error: ${typeof data?.detail === "string"
                    ? data.detail
                    : JSON.stringify(data?.detail || data || "Request failed")}`);
                return;
            }
            const userList = Array.isArray(data) ? data : (data?.users || []);
            setUsers(userList);
            const liveUser = userList.find((u) => String(u.email).toLowerCase() === String(selectedEmail).toLowerCase());
            if (!liveUser) {
                setActionMsg(`Live check: ${selectedEmail} was not found.`);
                return;
            }
            setActionMsg(`Live confirmed: ${liveUser.email} • ${liveUser.role} • ${liveUser.status}`);
            await loadAll();
        }
        catch (err) {
            setActionMsg(err?.message || "Live confirmation failed.");
        }
    }
    async function loadApprovalQueue() {
        try {
            setApprovalsLoading(true);
            setApprovalMessage("");
            const res = await fetch(`${API_BASE}/api/v1/chat/approvals?limit=100`, {
                method: "GET",
                credentials: "include",
                headers,
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setApprovals([]);
                setApprovalMessage(data?.detail ||
                    data?.error ||
                    "Unable to load owner approval requests.");
                return;
            }
            if (data?.execution_allowed !== false ||
                data?.executor_called !== false) {
                setApprovals([]);
                setApprovalMessage("Approval safety contract mismatch. Review controls are disabled.");
                return;
            }
            setApprovals(Array.isArray(data?.items)
                ? data.items
                : []);
        }
        catch (err) {
            setApprovals([]);
            setApprovalMessage(err?.message ||
                "Owner approval queue request failed.");
        }
        finally {
            setApprovalsLoading(false);
        }
    }
    async function reviewApproval(approvalId, decision) {
        const cleanId = String(approvalId || "").trim();
        if (!cleanId) {
            setApprovalMessage("Approval request identifier is missing.");
            return;
        }
        const confirmed = window.confirm(`${decision === "approved"
            ? "APPROVE"
            : "REJECT"} this request?\n\n` +
            `Approval ID: ${cleanId}\n\n` +
            "This records your review decision only. " +
            "It does NOT execute the action.");
        if (!confirmed) {
            return;
        }
        try {
            setApprovalBusyId(cleanId);
            setApprovalMessage("");
            const res = await fetch(`${API_BASE}/api/v1/chat/approvals/review`, {
                method: "POST",
                credentials: "include",
                headers: {
                    ...headers,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    approval_id: cleanId,
                    decision,
                    note: "Reviewed from AVAAIO Operations Console.",
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setApprovalMessage(data?.detail ||
                    data?.error ||
                    "Unable to record approval review.");
                return;
            }
            if (data?.execution_allowed !== false ||
                data?.executor_called !== false) {
                setApprovalMessage("Unexpected execution state returned. No further action was requested.");
                return;
            }
            setApprovalMessage(decision === "approved"
                ? "Approval recorded. No action was executed."
                : "Rejection recorded. No action was executed.");
            await loadApprovalQueue();
        }
        catch (err) {
            setApprovalMessage(err?.message ||
                "Approval review request failed.");
        }
        finally {
            setApprovalBusyId("");
        }
    }
    const currentRole = String(storedUser?.role || "admin").toUpperCase();
    const elevationSecondsLeft = Math.max(0, Number(elevation?.elevated_until || 0) - nowEpoch);
    const isElevated = Boolean(elevation?.elevated &&
        elevationSecondsLeft > 0);
    useEffect(() => {
        if (isElevated)
            return;
        setStatus(null);
        setUsers([]);
        setInvites([]);
        setUserActivity(null);
        setSelectedEmail("");
        setLoading(false);
    }, [isElevated]);
    useEffect(() => {
        if (!isElevated) {
            setApprovals([]);
            setApprovalMessage("");
            return;
        }
        if (adminSection === "approvals") {
            void loadApprovalQueue();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminSection, isElevated]);
    const elevationMinutes = Math.floor(elevationSecondsLeft / 60);
    const elevationSeconds = String(elevationSecondsLeft % 60).padStart(2, "0");
    return (_jsxs(AppPageShell, { kicker: isElevated ? "AVAAIO ADMIN" : "Ava", title: isElevated ? "Operations Console" : "Admin Console", subtitle: isElevated
            ? "System control, intelligence, users, security, and platform operations."
            : "Secure control center for users, access, AvaBrain, activity, and platform operations.", maxWidth: "1320px", children: [_jsxs("div", { style: styles.topRow, children: [_jsx("div", { style: styles.pill, children: isElevated
                            ? `${currentRole} • ELEVATED`
                            : `Current Access: ${currentRole}` }), _jsx("button", { type: "button", style: pageStyles.button, disabled: !isElevated || loading, onClick: () => void loadAll(), children: isElevated ? "Refresh" : "Admin Locked" })] }), _jsxs("div", { role: "status", style: {
                    marginBottom: "18px",
                    padding: "16px",
                    borderRadius: "16px",
                    border: isElevated
                        ? "1px solid rgba(110, 255, 170, 0.62)"
                        : "1px solid rgba(76, 201, 255, 0.48)",
                    background: isElevated
                        ? "rgba(20, 84, 55, 0.26)"
                        : "rgba(11, 42, 67, 0.52)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "16px",
                    flexWrap: "wrap",
                }, children: [_jsxs("div", { children: [_jsxs("div", { style: {
                                    fontWeight: 800,
                                    letterSpacing: "0.04em",
                                }, children: ["Super Admin Security: ", isElevated ? "ELEVATED" : "LOCKED"] }), _jsx("div", { style: {
                                    marginTop: "6px",
                                    opacity: 0.82,
                                    lineHeight: 1.5,
                                }, children: isElevated
                                    ? `Sensitive controls remain unlocked for ${elevationMinutes}:${elevationSeconds}.`
                                    : elevation?.eligible === false
                                        ? "This account is not the designated Super Admin account."
                                        : "Sensitive controls require the separate Admin PIN." }), elevationMessage ? (_jsx("div", { style: {
                                    marginTop: "8px",
                                    fontSize: "0.92rem",
                                }, children: elevationMessage })) : null] }), _jsxs("div", { style: {
                            display: "flex",
                            gap: "10px",
                            flexWrap: "wrap",
                        }, children: [isElevated ? (_jsx("button", { type: "button", style: pageStyles.button, disabled: elevationBusy, onClick: () => void lockElevation(), children: elevationBusy ? "Locking..." : "Lock Admin" })) : (_jsx("button", { type: "button", style: pageStyles.button, disabled: elevationBusy ||
                                    elevation?.eligible === false, onClick: () => {
                                    setElevationMessage("");
                                    setElevationModalOpen(true);
                                }, children: "Unlock Admin" })), _jsx("button", { type: "button", style: pageStyles.button, disabled: elevationBusy, onClick: () => void loadElevationStatus(), children: "Check Security Status" })] })] }), elevationModalOpen ? (_jsx("div", { role: "dialog", "aria-modal": "true", "aria-labelledby": "admin-elevation-title", style: {
                    position: "fixed",
                    inset: 0,
                    zIndex: 10000,
                    background: "rgba(0, 7, 16, 0.82)",
                    backdropFilter: "blur(10px)",
                    display: "grid",
                    placeItems: "center",
                    padding: "20px",
                }, onMouseDown: (event) => {
                    if (event.target === event.currentTarget) {
                        setElevationModalOpen(false);
                        setElevationPin("");
                    }
                }, children: _jsxs("form", { onSubmit: (event) => {
                        event.preventDefault();
                        void submitElevationPin();
                    }, style: {
                        width: "min(440px, 100%)",
                        borderRadius: "20px",
                        border: "1px solid rgba(76, 201, 255, 0.58)",
                        background: "rgba(7, 20, 35, 0.98)",
                        boxShadow: "0 0 42px rgba(30, 168, 255, 0.24)",
                        padding: "24px",
                    }, children: [_jsx("h2", { id: "admin-elevation-title", style: {
                                margin: 0,
                                fontSize: "1.35rem",
                            }, children: "Unlock Super Admin Controls" }), _jsx("p", { style: {
                                margin: "10px 0 18px",
                                opacity: 0.78,
                                lineHeight: 1.55,
                            }, children: "Enter the separate Admin PIN. The elevated session automatically expires after ten minutes." }), _jsx("label", { htmlFor: "admin-elevation-pin", style: {
                                display: "block",
                                marginBottom: "8px",
                                fontWeight: 700,
                            }, children: "Admin PIN" }), _jsx("input", { id: "admin-elevation-pin", type: "password", autoFocus: true, autoComplete: "off", value: elevationPin, onChange: (event) => setElevationPin(event.target.value), style: {
                                width: "100%",
                                boxSizing: "border-box",
                                borderRadius: "12px",
                                border: "1px solid rgba(76, 201, 255, 0.56)",
                                background: "rgba(0, 8, 18, 0.86)",
                                color: "white",
                                padding: "13px 14px",
                                fontSize: "1rem",
                                outline: "none",
                            } }), elevationMessage ? (_jsx("div", { style: {
                                marginTop: "12px",
                                lineHeight: 1.45,
                            }, children: elevationMessage })) : null, _jsxs("div", { style: {
                                marginTop: "20px",
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "10px",
                                flexWrap: "wrap",
                            }, children: [_jsx("button", { type: "button", style: pageStyles.button, disabled: elevationBusy, onClick: () => {
                                        setElevationModalOpen(false);
                                        setElevationPin("");
                                    }, children: "Cancel" }), _jsx("button", { type: "submit", style: pageStyles.button, disabled: elevationBusy ||
                                        !elevationPin.trim(), children: elevationBusy ? "Verifying..." : "Unlock" })] })] }) })) : null, _jsxs("div", { style: {
                    position: "relative",
                    minHeight: isElevated ? "0px" : "220px",
                }, children: [_jsxs("div", { inert: !isElevated, "aria-hidden": !isElevated, style: {
                            display: isElevated ? "block" : "none",
                            filter: isElevated
                                ? "none"
                                : "blur(15px)",
                            opacity: isElevated
                                ? 1
                                : 0.2,
                            pointerEvents: isElevated
                                ? "auto"
                                : "none",
                            userSelect: isElevated
                                ? "auto"
                                : "none",
                            transform: isElevated
                                ? "none"
                                : "scale(0.992)",
                            transition: "filter 240ms ease, opacity 240ms ease, transform 240ms ease",
                        }, children: [loading ? _jsx("div", { style: styles.notice, children: "Loading admin console..." }) : null, error ? _jsx("div", { style: styles.error, children: error }) : null, actionMsg ? _jsx("div", { style: styles.notice, children: actionMsg }) : null, _jsx("style", { children: `
        /* AVAAIO_ADMIN_MOBILE_V31 */
        @media (max-width: 760px) {
          .avaaio-admin-v3-grid {
            grid-template-columns: minmax(0, 1fr) !important;
            gap: 12px !important;
            width: 100% !important;
          }

          .avaaio-admin-v3-grid > aside {
            grid-column: 1 !important;
            position: static !important;
            width: 100% !important;
            display: grid !important;
            grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
            gap: 6px !important;
            padding: 10px !important;
          }

          .avaaio-admin-v3-grid > aside > div {
            grid-column: 1 / -1 !important;
          }

          .avaaio-admin-v3-grid > aside button {
            margin-bottom: 0 !important;
            min-width: 0 !important;
            white-space: normal !important;
          }

          .avaaio-admin-v3-content {
            grid-column: 1 !important;
            width: 100% !important;
            min-width: 0 !important;
          }

          .avaaio-admin-v3-grid input,
          .avaaio-admin-v3-grid select,
          .avaaio-admin-v3-grid textarea {
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
        }

        @media (max-width: 560px) {
          .avaaio-admin-v3-overview-cards {
            grid-template-columns: minmax(0, 1fr) !important;
          }
        }

        /* AVAAIO_ADMIN_PORTRAIT_V32 */
        @media (max-width: 480px) and (orientation: portrait) {
          .avaaio-admin-v3-grid {
            gap: 10px !important;
          }

          .avaaio-admin-v3-grid > aside {
            grid-template-columns: minmax(0, 1fr) !important;
          }

          .avaaio-admin-v3-grid > aside button {
            min-height: 44px !important;
            padding: 11px 12px !important;
            font-size: 14px !important;
          }

          .avaaio-admin-v3-grid input,
          .avaaio-admin-v3-grid select,
          .avaaio-admin-v3-grid textarea {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            font-size: 16px !important;
          }
        }
      ` }), _jsxs("div", { className: "avaaio-admin-v3-grid", style: {
                                    display: "grid",
                                    gridTemplateColumns: "210px minmax(0, 1fr)",
                                    gap: "14px",
                                    alignItems: "start",
                                }, children: [_jsxs("aside", { style: {
                                            gridColumn: "1",
                                            position: "sticky",
                                            top: "12px",
                                            padding: "14px",
                                            borderRadius: "16px",
                                            border: "1px solid rgba(103,232,249,0.16)",
                                            background: "linear-gradient(180deg,rgba(7,24,39,0.96),rgba(3,12,23,0.94))",
                                            boxSizing: "border-box",
                                        }, children: [_jsx("div", { style: {
                                                    fontSize: "17px",
                                                    fontWeight: 950,
                                                    letterSpacing: "0.12em",
                                                    color: "#aefaff",
                                                }, children: "AVAAIO" }), _jsx("div", { style: {
                                                    marginTop: "4px",
                                                    marginBottom: "14px",
                                                    fontSize: "10px",
                                                    fontWeight: 800,
                                                    letterSpacing: "0.14em",
                                                    color: "rgba(235,252,255,0.48)",
                                                }, children: "OPERATIONS" }), [
                                                ["overview", "Overview"],
                                                ["users", "Users"],
                                                ["pro", "Pro Access"],
                                                ["brain", "Brain & AI"],
                                                ["activity", "Activity"],
                                                ["librarian", "Librarian"],
                                                ["approvals", "Approvals"],
                                                ["security", "Security"],
                                            ].map(([id, label]) => (_jsxs("button", { type: "button", onClick: () => setAdminSection(id), style: {
                                                    width: "100%",
                                                    display: "block",
                                                    padding: "10px 11px",
                                                    marginBottom: "6px",
                                                    borderRadius: "10px",
                                                    border: adminSection === id
                                                        ? "1px solid rgba(103,232,249,0.30)"
                                                        : "1px solid transparent",
                                                    background: adminSection === id
                                                        ? "linear-gradient(135deg,rgba(0,234,255,0.13),rgba(93,121,255,0.08))"
                                                        : "transparent",
                                                    color: adminSection === id
                                                        ? "#efffff"
                                                        : "rgba(235,252,255,0.68)",
                                                    fontSize: "13px",
                                                    fontWeight: 800,
                                                    textAlign: "left",
                                                    cursor: "pointer",
                                                }, children: [label, id === "approvals" &&
                                                        ownerAttention?.attention_required ? (_jsx("span", { "aria-label": "Ava owner attention required", style: {
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            minWidth: "20px",
                                                            height: "20px",
                                                            marginLeft: "8px",
                                                            padding: "0 6px",
                                                            borderRadius: "999px",
                                                            fontSize: "10px",
                                                            fontWeight: 950,
                                                            lineHeight: 1,
                                                            verticalAlign: "middle",
                                                            color: "#fff7e6",
                                                            border: "1px solid rgba(255,190,90,0.65)",
                                                            background: "rgba(255,145,40,0.22)",
                                                            boxShadow: "0 0 14px rgba(255,145,40,0.18)",
                                                        }, children: Math.max(1, Number(ownerAttention.pending_approvals || 0) +
                                                            Number(ownerAttention.health_attention_count || 0)) })) : null] }, id)))] }), _jsxs("div", { className: "avaaio-admin-v3-content", style: {
                                            gridColumn: "2",
                                            display: "grid",
                                            gap: "14px",
                                            minWidth: 0,
                                        }, children: [adminSection === "overview" ? (_jsxs(_Fragment, { children: [_jsx("div", { style: {
                                                            fontSize: "18px",
                                                            fontWeight: 900,
                                                            letterSpacing: "0.04em",
                                                            color: "#f3ffff",
                                                        }, children: "Platform Overview" }), _jsx("div", { className: "avaaio-admin-v3-overview-cards", style: {
                                                            display: "grid",
                                                            gridTemplateColumns: "repeat(auto-fit, minmax(165px, 1fr))",
                                                            gap: "12px",
                                                        }, children: [
                                                            [
                                                                "USERS",
                                                                String((users || []).length),
                                                                "Registered accounts",
                                                            ],
                                                            [
                                                                "ONLINE NOW",
                                                                String(userActivity?.online_now ?? 0),
                                                                "Current activity",
                                                            ],
                                                            [
                                                                "AVA BRAIN",
                                                                String(status?.brain?.active_provider || "CHECKING").toUpperCase(),
                                                                "Active provider",
                                                            ],
                                                            [
                                                                "SYSTEM",
                                                                userActivity?.ok
                                                                    ? "HEALTHY"
                                                                    : status
                                                                        ? "READY"
                                                                        : "CHECKING",
                                                                "Platform operations",
                                                            ],
                                                        ].map(([label, value, meta]) => (_jsxs("div", { style: {
                                                                minHeight: "106px",
                                                                padding: "15px",
                                                                borderRadius: "14px",
                                                                border: "1px solid rgba(103,232,249,0.14)",
                                                                background: "linear-gradient(145deg,rgba(10,34,51,0.86),rgba(5,16,28,0.92))",
                                                            }, children: [_jsx("div", { style: {
                                                                        fontSize: "10px",
                                                                        fontWeight: 900,
                                                                        letterSpacing: "0.12em",
                                                                        color: "rgba(185,248,255,0.62)",
                                                                    }, children: label }), _jsx("div", { style: {
                                                                        marginTop: "8px",
                                                                        fontSize: label === "USERS" ||
                                                                            label === "ONLINE NOW"
                                                                            ? "27px"
                                                                            : "16px",
                                                                        fontWeight: 950,
                                                                        color: "#eaffff",
                                                                    }, children: value }), _jsx("div", { style: {
                                                                        marginTop: "8px",
                                                                        fontSize: "11px",
                                                                        color: "rgba(235,252,255,0.52)",
                                                                    }, children: meta })] }, label))) })] })) : null, adminSection === "approvals" ? (_jsxs(AppSectionCard, { children: [_jsxs("div", { style: {
                                                            display: "flex",
                                                            justifyContent: "space-between",
                                                            gap: "12px",
                                                            alignItems: "flex-start",
                                                            flexWrap: "wrap",
                                                        }, children: [_jsxs("div", { children: [_jsx("div", { style: styles.sectionTitle, children: "Owner Approval Queue" }), _jsx("div", { style: styles.note, children: "Approve or Reject records your review decision only. Nothing here can claim, execute, install, restart, or repair." })] }), _jsx("button", { type: "button", style: pageStyles.button, disabled: approvalsLoading, onClick: () => void loadApprovalQueue(), children: approvalsLoading
                                                                    ? "Refreshing..."
                                                                    : "Refresh Approvals" })] }), approvalMessage ? (_jsx("div", { style: {
                                                            marginTop: "14px",
                                                            padding: "10px 12px",
                                                            borderRadius: "10px",
                                                            border: "1px solid rgba(103,232,249,0.18)",
                                                            background: "rgba(103,232,249,0.05)",
                                                            lineHeight: 1.5,
                                                        }, children: approvalMessage })) : null, _jsxs("div", { style: {
                                                            display: "grid",
                                                            gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))",
                                                            gap: "10px",
                                                            marginTop: "16px",
                                                        }, children: [_jsxs("div", { style: {
                                                                    padding: "12px",
                                                                    borderRadius: "12px",
                                                                    border: "1px solid rgba(255,255,255,0.09)",
                                                                }, children: [_jsx("div", { style: styles.statLabel, children: "Pending" }), _jsx("div", { style: styles.statValue, children: approvals.filter((item) => String(item?.status || "").toLowerCase() === "pending").length })] }), _jsxs("div", { style: {
                                                                    padding: "12px",
                                                                    borderRadius: "12px",
                                                                    border: "1px solid rgba(255,255,255,0.09)",
                                                                }, children: [_jsx("div", { style: styles.statLabel, children: "Total Records" }), _jsx("div", { style: styles.statValue, children: approvals.length })] }), _jsxs("div", { style: {
                                                                    padding: "12px",
                                                                    borderRadius: "12px",
                                                                    border: "1px solid rgba(255,255,255,0.09)",
                                                                }, children: [_jsx("div", { style: styles.statLabel, children: "Execution" }), _jsx("div", { style: styles.statValue, children: "DISABLED" })] })] }), !approvalsLoading &&
                                                        approvals.length === 0 ? (_jsx("div", { style: {
                                                            marginTop: "16px",
                                                            opacity: 0.72,
                                                        }, children: "No approval records are currently available." })) : null, _jsx("div", { style: {
                                                            display: "grid",
                                                            gap: "12px",
                                                            marginTop: "16px",
                                                        }, children: approvals.map((item) => {
                                                            const approvalId = String(item?.approval_id || "");
                                                            const status = String(item?.status || "unknown").toLowerCase();
                                                            const createdAt = Number(item?.created_at || 0);
                                                            const expiresAt = Number(item?.expires_at || 0);
                                                            const expired = status === "expired" ||
                                                                Boolean(expiresAt &&
                                                                    nowEpoch >= expiresAt);
                                                            const canReview = status === "pending" &&
                                                                !expired &&
                                                                Boolean(approvalId);
                                                            const busy = approvalBusyId === approvalId;
                                                            const payload = item?.action_payload ??
                                                                item?.action_payload_json ??
                                                                {};
                                                            const payloadText = typeof payload === "string"
                                                                ? payload
                                                                : JSON.stringify(payload, null, 2);
                                                            return (_jsxs("div", { style: {
                                                                    padding: "16px",
                                                                    borderRadius: "14px",
                                                                    border: status === "pending"
                                                                        ? "1px solid rgba(103,232,249,0.28)"
                                                                        : "1px solid rgba(255,255,255,0.09)",
                                                                    background: "rgba(255,255,255,0.025)",
                                                                }, children: [_jsxs("div", { style: {
                                                                            display: "flex",
                                                                            justifyContent: "space-between",
                                                                            gap: "10px",
                                                                            flexWrap: "wrap",
                                                                        }, children: [_jsx("strong", { style: {
                                                                                    overflowWrap: "anywhere",
                                                                                }, children: String(item?.action_type ||
                                                                                    "Unknown action")
                                                                                    .replaceAll("_", " ")
                                                                                    .toUpperCase() }), _jsx("strong", { children: expired
                                                                                    ? "EXPIRED"
                                                                                    : status.toUpperCase() })] }), _jsxs("div", { style: {
                                                                            display: "grid",
                                                                            gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
                                                                            gap: "10px",
                                                                            marginTop: "14px",
                                                                        }, children: [_jsxs("div", { children: [_jsx("div", { style: styles.statLabel, children: "Risk" }), _jsx("div", { style: styles.statValue, children: String(item?.risk_level ||
                                                                                            "unknown").toUpperCase() })] }), _jsxs("div", { children: [_jsx("div", { style: styles.statLabel, children: "Super Admin" }), _jsx("div", { style: styles.statValue, children: item?.requires_super_admin
                                                                                            ? "REQUIRED"
                                                                                            : "NOT REQUIRED" })] }), _jsxs("div", { children: [_jsx("div", { style: styles.statLabel, children: "Requested By" }), _jsx("div", { style: {
                                                                                            marginTop: "5px",
                                                                                            overflowWrap: "anywhere",
                                                                                        }, children: item?.requester_email ||
                                                                                            "Authenticated requester" })] })] }), _jsxs("div", { style: { marginTop: "14px" }, children: [_jsx("div", { style: styles.statLabel, children: "Reason" }), _jsx("div", { style: {
                                                                                    marginTop: "5px",
                                                                                    lineHeight: 1.5,
                                                                                }, children: item?.reason ||
                                                                                    "No reason supplied." })] }), _jsxs("div", { style: { marginTop: "14px" }, children: [_jsx("div", { style: styles.statLabel, children: "Action Payload" }), _jsx("pre", { style: {
                                                                                    margin: "6px 0 0",
                                                                                    padding: "10px",
                                                                                    borderRadius: "10px",
                                                                                    background: "rgba(0,0,0,0.25)",
                                                                                    whiteSpace: "pre-wrap",
                                                                                    overflowWrap: "anywhere",
                                                                                    maxHeight: "180px",
                                                                                    overflow: "auto",
                                                                                    fontSize: "11px",
                                                                                }, children: payloadText || "{}" })] }), _jsxs("div", { style: {
                                                                            marginTop: "14px",
                                                                            fontSize: "11px",
                                                                            lineHeight: 1.6,
                                                                            overflowWrap: "anywhere",
                                                                        }, children: [_jsxs("div", { children: ["Approval ID: ", approvalId || "—"] }), _jsxs("div", { children: ["Created:", " ", createdAt
                                                                                        ? new Date(createdAt * 1000).toLocaleString()
                                                                                        : "—"] }), _jsxs("div", { children: ["Expires:", " ", expiresAt
                                                                                        ? new Date(expiresAt * 1000).toLocaleString()
                                                                                        : "—"] })] }), item?.review_decision ? (_jsxs("div", { style: {
                                                                            marginTop: "12px",
                                                                            padding: "9px",
                                                                            borderRadius: "9px",
                                                                            background: "rgba(255,255,255,0.04)",
                                                                        }, children: ["Review:", " ", _jsx("strong", { children: String(item.review_decision).toUpperCase() }), item?.review_note
                                                                                ? ` • ${item.review_note}`
                                                                                : ""] })) : null, _jsxs("div", { style: {
                                                                            display: "flex",
                                                                            gap: "10px",
                                                                            flexWrap: "wrap",
                                                                            marginTop: "15px",
                                                                        }, children: [_jsx("button", { type: "button", style: pageStyles.button, disabled: !canReview || busy, onClick: () => void reviewApproval(approvalId, "approved"), children: busy
                                                                                    ? "Recording..."
                                                                                    : "Approve" }), _jsx("button", { type: "button", style: {
                                                                                    ...pageStyles.button,
                                                                                    border: "1px solid rgba(255,120,120,0.55)",
                                                                                }, disabled: !canReview || busy, onClick: () => void reviewApproval(approvalId, "rejected"), children: busy
                                                                                    ? "Recording..."
                                                                                    : "Reject" })] }), !canReview ? (_jsx("div", { style: {
                                                                            marginTop: "9px",
                                                                            fontSize: "11px",
                                                                            opacity: 0.65,
                                                                        }, children: "This request is not reviewable." })) : null] }, approvalId));
                                                        }) })] })) : null, adminSection === "security" ? (_jsxs(AppSectionCard, { children: [_jsx("div", { style: styles.sectionTitle, children: "Security" }), _jsxs("div", { style: styles.statRow, children: [_jsx("div", { style: styles.statLabel, children: "Super Admin" }), _jsx("div", { style: styles.statValue, children: isElevated ? "ELEVATED" : "LOCKED" })] }), _jsxs("div", { style: styles.statRow, children: [_jsx("div", { style: styles.statLabel, children: "Elevated Session" }), _jsx("div", { style: styles.statValue, children: isElevated
                                                                    ? `${elevationMinutes}:${elevationSeconds}`
                                                                    : "INACTIVE" })] }), _jsx("div", { style: styles.note, children: "Sensitive operations remain protected by the Super Admin elevation session." })] })) : null, adminSection === "brain" ? (_jsxs(_Fragment, { children: [_jsxs(AppSectionCard, { children: [_jsx("div", { style: styles.sectionTitle, children: "Brain Status" }), _jsxs("div", { style: styles.badgeRow, children: [_jsxs("div", { style: styles.bigBadge, children: ["ACTIVE: ", status?.brain?.active_provider || "UNKNOWN"] }), _jsxs("div", { style: styles.smallBadge, children: ["FALLBACK: ", status?.brain?.fallback_provider || "NONE"] })] }), _jsxs("div", { style: styles.statRow, children: [_jsx("div", { style: styles.statLabel, children: "Preferred Provider" }), _jsx("div", { style: styles.statValue, children: String(status?.brain?.preferred_provider || "—").toUpperCase() })] }), _jsxs("div", { style: styles.statRow, children: [_jsx("div", { style: styles.statLabel, children: "Brain API" }), _jsx("div", { style: styles.statValue, children: status?.brain?.brain_api_online ? "ONLINE" : "OFFLINE" })] }), _jsxs("div", { style: styles.statRow, children: [_jsx("div", { style: styles.statLabel, children: "OpenAI" }), _jsx("div", { style: styles.statValue, children: status?.brain?.openai_configured ? "CONFIGURED" : "NOT CONFIGURED" })] }), _jsxs("div", { style: styles.statRow, children: [_jsx("div", { style: styles.statLabel, children: "Local Fallback Enabled" }), _jsx("div", { style: styles.statValue, children: status?.brain?.local_brain_enabled ? "YES" : "NO" })] }), _jsxs("div", { style: styles.statRow, children: [_jsx("div", { style: styles.statLabel, children: "Brain Endpoint" }), _jsx("div", { style: styles.statValue, children: status?.brain?.brain_base_url || "—" })] }), _jsx("div", { style: styles.note, children: status?.brain?.active_reason || "No brain details available." }), statusWarning ? _jsx("div", { style: styles.warnBox, children: statusWarning }) : null] }), _jsxs(AppSectionCard, { children: [_jsx("div", { style: styles.sectionTitle, children: "Brain Switcher" }), _jsx("label", { style: styles.inputLabel, children: "Preferred Provider" }), _jsxs("select", { value: preferredProvider, onChange: (e) => setPreferredProvider(e.target.value), style: styles.select, children: [_jsx("option", { value: "avabrain", children: "AvaBrain API" }), _jsx("option", { value: "openai", children: "OpenAI" }), _jsx("option", { value: "local", children: "Local Brain" })] }), _jsxs("label", { style: { ...styles.inputLabel, marginTop: "14px" }, children: [_jsx("input", { type: "checkbox", checked: localFallbackEnabled, onChange: (e) => setLocalFallbackEnabled(e.target.checked), style: { marginRight: "8px" } }), "Enable Local Fallback"] }), _jsx("div", { style: {
                                                                    display: "flex",
                                                                    gap: "10px",
                                                                    flexWrap: "wrap",
                                                                    marginTop: "16px",
                                                                }, children: _jsx("button", { type: "button", style: pageStyles.button, onClick: saveBrainControls, children: "Save Brain Controls" }) }), _jsx("div", { style: styles.sectionSubTitle, children: "Provider Matrix" }), (status?.providers || []).length ? ((status?.providers || []).map((provider) => (_jsxs("div", { style: styles.providerCard, children: [_jsx("div", { style: styles.providerName, children: provider.name }), _jsxs("div", { style: styles.providerMeta, children: ["Configured: ", provider.configured ? "Yes" : "No", " \u2022 Online:", " ", provider.online ? "Yes" : "No", " \u2022 Active:", " ", provider.active ? "Yes" : "No", " \u2022 Preferred:", " ", provider.preferred ? "Yes" : "No"] })] }, provider.name)))) : (_jsx("div", { style: styles.note, children: "No provider matrix available yet." }))] })] })) : null, adminSection === "activity" ? (_jsx(_Fragment, { children: _jsxs(AppSectionCard, { children: [_jsx("div", { style: { ...styles.sectionTitle, color: "#9ffcff" }, children: "Live User Activity" }), _jsx("div", { style: { ...styles.note, color: "#e8fbff", marginBottom: "12px" }, children: "Real-time admin visibility for recent platform activity." }), userActivity ? (_jsx("div", { style: {
                                                                border: "1px solid rgba(120, 245, 255, 0.35)",
                                                                background: "rgba(0, 255, 255, 0.06)",
                                                                borderRadius: "16px",
                                                                padding: "14px",
                                                                color: "#f4ffff",
                                                                fontSize: "13px",
                                                                lineHeight: 1.5,
                                                                maxHeight: "230px",
                                                                overflow: "auto",
                                                                boxShadow: "0 0 24px rgba(0, 255, 255, 0.10)",
                                                            }, children: _jsxs("div", { style: { display: "grid", gap: 16 }, children: [_jsxs("div", { style: {
                                                                            display: "grid",
                                                                            gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                                                                            gap: 12,
                                                                        }, children: [_jsxs("div", { style: {
                                                                                    padding: 14,
                                                                                    borderRadius: 16,
                                                                                    background: "rgba(0,255,255,0.08)",
                                                                                    border: "1px solid rgba(143,247,255,0.18)"
                                                                                }, children: [_jsx("div", { style: { fontSize: 12, opacity: 0.72, marginBottom: 6 }, children: "Total Users" }), _jsx("div", { style: { fontSize: 26, fontWeight: 900, color: "#eaffff" }, children: userActivity?.total_users ?? 0 })] }), _jsxs("div", { style: {
                                                                                    padding: 14,
                                                                                    borderRadius: 16,
                                                                                    background: "rgba(0,255,180,0.08)",
                                                                                    border: "1px solid rgba(0,255,180,0.18)"
                                                                                }, children: [_jsx("div", { style: { fontSize: 12, opacity: 0.72, marginBottom: 6 }, children: "Online Now" }), _jsx("div", { style: { fontSize: 26, fontWeight: 900, color: "#9ffcff" }, children: userActivity?.online_now ?? 0 })] }), _jsxs("div", { style: {
                                                                                    padding: 14,
                                                                                    borderRadius: 16,
                                                                                    background: "rgba(255,255,255,0.05)",
                                                                                    border: "1px solid rgba(255,255,255,0.12)"
                                                                                }, children: [_jsx("div", { style: { fontSize: 12, opacity: 0.72, marginBottom: 6 }, children: "Status" }), _jsx("div", { style: { fontSize: 16, fontWeight: 800, color: userActivity?.ok ? "#9ffcff" : "#ff9f9f" }, children: userActivity?.ok ? "Healthy" : "Needs Attention" })] })] }), _jsxs("div", { style: {
                                                                            padding: 16,
                                                                            borderRadius: 18,
                                                                            background: "rgba(2,8,18,0.58)",
                                                                            border: "1px solid rgba(143,247,255,0.14)"
                                                                        }, children: [_jsx("div", { style: { fontSize: 13, fontWeight: 800, color: "#8ff7ff", marginBottom: 12 }, children: "Activity Timeline" }), _jsx("div", { style: { display: "grid", gap: 9 }, children: (userActivity?.timeline || []).map((item, idx) => {
                                                                                    const maxCount = Math.max(1, ...(userActivity?.timeline || []).map((x) => Number(x.count || 0)));
                                                                                    const width = Math.max(4, Math.round((Number(item.count || 0) / maxCount) * 100));
                                                                                    return (_jsxs("div", { style: { display: "grid", gridTemplateColumns: "52px 1fr 34px", gap: 10, alignItems: "center" }, children: [_jsx("div", { style: { fontSize: 12, color: "rgba(234,255,255,0.68)" }, children: item.hour }), _jsx("div", { style: { height: 9, borderRadius: 999, background: "rgba(255,255,255,0.08)", overflow: "hidden" }, children: _jsx("div", { style: {
                                                                                                        height: "100%",
                                                                                                        width: `${width}%`,
                                                                                                        borderRadius: 999,
                                                                                                        background: "linear-gradient(90deg, #00eaff, #8ff7ff)",
                                                                                                        boxShadow: "0 0 14px rgba(0,255,255,0.35)"
                                                                                                    } }) }), _jsx("div", { style: { fontSize: 12, color: "#eaffff", textAlign: "right", fontWeight: 800 }, children: item.count })] }, `${item.hour}-${idx}`));
                                                                                }) })] })] }) })) : (_jsxs("div", { style: {
                                                                border: "1px solid rgba(255,255,255,0.16)",
                                                                background: "rgba(255,255,255,0.05)",
                                                                borderRadius: "16px",
                                                                padding: "14px",
                                                                color: "#d9faff",
                                                                fontSize: "13px",
                                                                textAlign: "center",
                                                                minHeight: "150px",
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                            }, children: [_jsx("div", { style: { fontSize: "32px", marginBottom: "8px", opacity: 0.85 }, children: "\uD83D\uDCC8" }), _jsx("div", { style: { fontWeight: 800, marginBottom: "4px" }, children: "No live activity loaded yet." }), _jsx("div", { style: { opacity: 0.82, marginBottom: "12px" }, children: "Click Refresh after signing in as admin." }), _jsx("button", { type: "button", style: pageStyles.button, onClick: loadAll, children: "Refresh Activity" })] }))] }) })) : null, adminSection === "librarian" ? (_jsx(_Fragment, { children: _jsx(LocalLibrarianPanel, { isElevated: isElevated }) })) : null, adminSection === "pro" ? (_jsx(_Fragment, { children: _jsxs(AppSectionCard, { children: [_jsx("div", { style: styles.sectionTitle, children: "Pro Invitation Center" }), _jsx("input", { value: inviteEmail, onChange: (e) => setInviteEmail(e.target.value), placeholder: "user@email.com", style: styles.input }), _jsx("input", { value: inviteDays, onChange: (e) => setInviteDays(e.target.value), placeholder: "5", style: { ...styles.input, marginTop: "10px" } }), _jsxs("div", { style: {
                                                                display: "flex",
                                                                gap: "10px",
                                                                flexWrap: "wrap",
                                                                marginTop: "12px",
                                                            }, children: [_jsx("button", { type: "button", style: pageStyles.button, onClick: () => postAction("/api/v1/admin/send-invite", {
                                                                        email: inviteEmail,
                                                                        expires_days: Number(inviteDays || 5),
                                                                        lifetime_vip: true,
                                                                    }), children: "Send Pro Invitation" }), _jsx("button", { type: "button", style: pageStyles.button, onClick: () => {
                                                                        const cleanInviteEmail = String(inviteEmail || "").trim().toLowerCase();
                                                                        if (!cleanInviteEmail || !cleanInviteEmail.includes("@")) {
                                                                            setActionMsg("❌ Enter or click a valid invite email first.");
                                                                            return;
                                                                        }
                                                                        const ok = window.confirm(`Revoke Pro invitation?\n\nEmail: ${cleanInviteEmail}\n\nContinue?`);
                                                                        if (!ok) {
                                                                            setActionMsg("Invite revoke cancelled.");
                                                                            return;
                                                                        }
                                                                        postAction("/api/v1/admin/revoke-invite", {
                                                                            email: cleanInviteEmail,
                                                                        });
                                                                    }, children: "Revoke Pro Invitation" }), _jsx("button", { type: "button", style: {
                                                                        ...pageStyles.button,
                                                                        border: "1px solid rgba(255,140,140,0.6)",
                                                                        color: "#ffd0d0"
                                                                    }, onClick: async () => {
                                                                        if (!window.confirm("Clear old/revoked invite history?")) {
                                                                            return;
                                                                        }
                                                                        await postAction("/api/v1/admin/clear-invite-history", {});
                                                                        await loadAll();
                                                                    }, children: "Clear History" })] }), _jsx("div", { style: styles.sectionSubTitle, children: "Recent Invites" }), _jsx("div", { style: styles.tableWrap, children: (invites || []).length ? ((invites || []).slice(0, 8).map((invite) => (_jsx("div", { style: { ...styles.row, cursor: "pointer" }, onClick: () => {
                                                                    setInviteEmail(String(invite.email || "").trim().toLowerCase());
                                                                    setActionMsg(`Selected invite: ${invite.email}`);
                                                                }, children: _jsxs("div", { style: styles.rowMain, children: [_jsx("div", { style: styles.rowTitle, children: invite.email }), _jsxs("div", { style: styles.rowMeta, children: [invite.status || "pending", " \u2022 expires: ", invite.expires_at || "—"] })] }) }, invite.id)))) : (_jsx("div", { style: styles.note, children: "No invites found yet." })) }), invitesWarning ? _jsx("div", { style: styles.warnBox, children: invitesWarning }) : null] }) })) : null, adminSection === "users" ? (_jsx(_Fragment, { children: _jsxs(AppSectionCard, { children: [_jsx("div", { style: styles.sectionTitle, children: "User Actions" }), _jsx("input", { value: selectedEmail, onChange: (e) => setSelectedEmail(e.target.value), placeholder: "Select or type user email", style: styles.input }), _jsx("div", { style: styles.tableWrap, children: (users || []).length ? ((users || []).slice(0, 10).map((user) => (_jsxs("div", { style: {
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "center",
                                                                    gap: "10px",
                                                                    padding: "8px 10px",
                                                                    marginBottom: "6px",
                                                                    borderRadius: "10px",
                                                                    background: selectedEmail === user.email
                                                                        ? "rgba(0,255,255,0.08)"
                                                                        : "rgba(255,255,255,0.03)",
                                                                    cursor: "pointer"
                                                                }, onClick: () => setSelectedEmail(user.email), children: [_jsxs("span", { style: { fontSize: "12px", color: "#ccc" }, children: [user.email, " \u2022 ", user.role, " \u2022 ", user.status] }), selectedEmail === user.email && (_jsx("span", { style: {
                                                                            color: "#00ffff",
                                                                            fontSize: "11px",
                                                                            whiteSpace: "nowrap",
                                                                            marginLeft: "10px",
                                                                            flexShrink: 0
                                                                        }, children: "SELECTED" }))] }, user.email)))) : (_jsx("div", { style: styles.note, children: "No users loaded yet." })) }), _jsxs("div", { style: { marginTop: "12px", color: "#aaa", fontSize: "12px" }, children: ["Selected: ", selectedEmail || "None"] }), _jsxs("div", { style: {
                                                                display: "flex",
                                                                gap: "10px",
                                                                flexWrap: "wrap",
                                                                marginTop: "12px",
                                                            }, children: [_jsx("button", { style: pageStyles.button, onClick: () => confirmUserAction("Grant Pro Access", "/api/v1/admin/grant-vip"), children: "Grant Pro Access" }), _jsx("button", { style: pageStyles.button, onClick: () => confirmUserAction("Remove Admin-Granted Pro", "/api/v1/admin/revoke-vip"), children: "Remove Admin-Granted Pro" }), _jsx("button", { style: pageStyles.button, onClick: () => confirmUserAction("Promote to Admin", "/api/v1/admin/promote-admin", true), children: "Promote to Admin" }), _jsx("button", { style: pageStyles.button, onClick: () => confirmUserAction("Reset to Free User", "/api/v1/admin/demote-admin", true), children: "Reset to Free User" }), _jsx("button", { style: pageStyles.button, onClick: () => confirmUserAction("Ban Account", "/api/v1/admin/ban", true), children: "Ban Account" }), _jsx("button", { style: pageStyles.button, onClick: () => confirmUserAction("Restore Account", "/api/v1/admin/unban"), children: "Restore Account" }), _jsx("button", { style: {
                                                                        ...pageStyles.button,
                                                                        border: "1px solid rgba(255,70,70,0.75)",
                                                                        color: "#ff9b9b"
                                                                    }, onClick: () => {
                                                                        if (!selectedEmail) {
                                                                            setActionMsg("Select a user first.");
                                                                            return;
                                                                        }
                                                                        const ok = window.confirm(`⚠️ Delete User Permanently\n\nUser: ${selectedEmail}\n\nThis cannot be undone. Continue?`);
                                                                        if (!ok)
                                                                            return;
                                                                        postAction("/api/v1/admin/delete-user", { email: selectedEmail }, "DELETE");
                                                                    }, children: "Delete User" })] })] }) })) : null] })] })] }), !isElevated ? (_jsxs("div", { role: "status", "aria-live": "polite", style: {
                            position: "absolute",
                            zIndex: 20,
                            top: "34px",
                            left: "50%",
                            width: "min(520px, calc(100% - 32px))",
                            transform: "translateX(-50%)",
                            boxSizing: "border-box",
                            borderRadius: "20px",
                            border: "1px solid rgba(76, 201, 255, 0.66)",
                            background: "rgba(5, 18, 33, 0.96)",
                            boxShadow: "0 0 48px rgba(30, 168, 255, 0.3)",
                            padding: "24px",
                            textAlign: "center",
                            backdropFilter: "blur(16px)",
                        }, children: [_jsx("div", { style: {
                                    fontSize: "1.15rem",
                                    fontWeight: 900,
                                    letterSpacing: "0.05em",
                                }, children: "ADMIN CONSOLE PROTECTED" }), _jsx("div", { style: {
                                    marginTop: "10px",
                                    lineHeight: 1.6,
                                    opacity: 0.82,
                                }, children: "User records, system controls, invitations, activity, and platform operations remain hidden until Super Admin verification is complete." }), _jsx("button", { type: "button", style: {
                                    ...pageStyles.button,
                                    marginTop: "18px",
                                }, disabled: elevationBusy ||
                                    elevation?.eligible === false, onClick: () => {
                                    setElevationMessage("");
                                    setElevationModalOpen(true);
                                }, children: "Unlock Admin Console" })] })) : null] })] }));
}
const styles = {
    topRow: {
        display: "flex",
        gap: "14px",
        flexWrap: "wrap",
        alignItems: "center",
        marginBottom: "14px",
        padding: "12px",
        borderRadius: "20px",
        border: "1px solid rgba(103,232,249,0.14)",
        background: "linear-gradient(135deg, rgba(0,255,255,0.07), rgba(255,255,255,0.025))",
        boxShadow: "0 18px 50px rgba(0,0,0,0.22)",
    },
    pill: {
        padding: "11px 16px",
        borderRadius: "999px",
        border: "1px solid rgba(103,232,249,0.45)",
        background: "linear-gradient(135deg, rgba(8,35,48,0.94), rgba(4,12,24,0.86))",
        color: "#bffcff",
        fontSize: "12px",
        fontWeight: 900,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        boxShadow: "inset 0 0 18px rgba(0,255,255,0.08)",
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
        gap: "14px",
        alignItems: "start",
        alignContent: "start",
    },
    sectionTitle: {
        fontSize: "18px",
        fontWeight: 950,
        color: "#f7ffff",
        letterSpacing: "0.055em",
        textTransform: "uppercase",
        marginBottom: "14px",
        textShadow: "0 0 18px rgba(103,232,249,0.18)",
    },
    sectionSubTitle: {
        fontSize: "13px",
        fontWeight: 950,
        color: "#67f7ff",
        letterSpacing: "0.09em",
        textTransform: "uppercase",
        marginTop: "20px",
        marginBottom: "12px",
    },
    badgeRow: {
        display: "flex",
        gap: "10px",
        flexWrap: "wrap",
        marginBottom: "12px",
    },
    bigBadge: {
        padding: "10px 14px",
        borderRadius: "14px",
        background: "linear-gradient(135deg, rgba(34,211,238,0.24), rgba(14,165,233,0.12))",
        border: "1px solid rgba(103,232,249,0.36)",
        color: "#ecfeff",
        fontWeight: 950,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        fontSize: "12px",
        boxShadow: "0 0 24px rgba(34,211,238,0.14)",
    },
    smallBadge: {
        padding: "10px 14px",
        borderRadius: "14px",
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.13)",
        color: "#ffffff",
        fontWeight: 850,
        letterSpacing: "0.07em",
        textTransform: "uppercase",
        fontSize: "12px",
    },
    statRow: {
        padding: "12px 0",
        borderBottom: "1px solid rgba(255,255,255,0.075)",
        display: "grid",
        gridTemplateColumns: "minmax(120px, 0.9fr) minmax(0, 1.1fr)",
        gap: "14px",
        alignItems: "center",
    },
    statLabel: {
        color: "#67f7ff",
        fontSize: "11px",
        fontWeight: 850,
        letterSpacing: "0.09em",
        textTransform: "uppercase",
        marginBottom: 0,
    },
    statValue: {
        color: "#ffffff",
        fontSize: "14px",
        lineHeight: 1.55,
        wordBreak: "break-word",
        textAlign: "right",
        fontWeight: 650,
    },
    inputLabel: {
        color: "#67e8f9",
        fontSize: "12px",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        display: "block",
    },
    input: {
        width: "100%",
        minHeight: "50px",
        borderRadius: "14px",
        border: "1px solid rgba(103,232,249,0.16)",
        background: "rgba(3,10,20,0.86)",
        color: "#fff",
        padding: "0 15px",
        boxSizing: "border-box",
        outline: "none",
        fontSize: "14px",
        boxShadow: "inset 0 0 18px rgba(0,0,0,0.22)",
    },
    select: {
        width: "100%",
        minHeight: "50px",
        borderRadius: "14px",
        border: "1px solid rgba(103,232,249,0.16)",
        background: "rgba(3,10,20,0.86)",
        color: "#fff",
        padding: "0 15px",
        boxSizing: "border-box",
        outline: "none",
        fontSize: "14px",
        marginTop: "8px",
        boxShadow: "inset 0 0 18px rgba(0,0,0,0.22)",
    },
    tableWrap: {
        marginTop: "12px",
        display: "flex",
        flexDirection: "column",
        gap: "9px",
        maxHeight: "285px",
        overflowY: "auto",
        paddingRight: "4px",
    },
    row: {
        padding: "12px 13px",
        borderRadius: "14px",
        border: "1px solid rgba(103,232,249,0.10)",
        background: "rgba(255,255,255,0.04)",
        boxShadow: "inset 0 0 16px rgba(255,255,255,0.015)",
    },
    rowMain: {
        display: "flex",
        flexDirection: "column",
        gap: "4px",
    },
    rowTitle: {
        color: "#ffffff",
        fontSize: "14px",
        fontWeight: 700,
    },
    rowMeta: {
        color: "rgba(255,255,255,0.70)",
        fontSize: "12px",
    },
    userPick: {
        textAlign: "left",
        border: "1px solid rgba(255,255,255,0.08)",
        background: "rgba(255,255,255,0.03)",
        color: "#fff",
        borderRadius: "12px",
        padding: "10px 12px",
        cursor: "pointer",
    },
    providerCard: {
        padding: "12px 0",
        borderBottom: "1px solid rgba(255,255,255,0.075)",
    },
    providerName: {
        color: "#ffffff",
        fontSize: "15px",
        fontWeight: 700,
        marginBottom: "6px",
    },
    providerMeta: {
        color: "rgba(255,255,255,0.78)",
        fontSize: "13px",
        lineHeight: 1.6,
    },
    note: {
        color: "rgba(235,252,255,0.82)",
        lineHeight: 1.7,
        fontSize: "14px",
        marginTop: "12px",
    },
    notice: {
        marginBottom: "16px",
        padding: "12px 14px",
        borderRadius: "14px",
        border: "1px solid rgba(103,232,249,0.18)",
        background: "rgba(34,211,238,0.06)",
        color: "#bff7ff",
    },
    error: {
        marginBottom: "16px",
        padding: "12px 14px",
        borderRadius: "14px",
        border: "1px solid rgba(255,120,120,0.25)",
        background: "rgba(255,80,80,0.08)",
        color: "#ffb3b3",
    },
    warnBox: {
        marginTop: "14px",
        padding: "11px 13px",
        borderRadius: "14px",
        border: "1px solid rgba(255,179,179,0.22)",
        background: "rgba(255,179,179,0.07)",
        color: "#ffb3b3",
        fontSize: "13px",
        lineHeight: 1.5,
    },
};
